import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// Office-hours slots: 10:00 AM – 6:30 PM, every 30 min
const SLOTS = [];
for (let h = 10; h <= 18; h += 1) {
  for (const m of [0, 30]) {
    const label = new Date(2000, 0, 1, h, m).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    SLOTS.push({ value: `${h}:${m}`, label });
  }
}

const calendarClassNames = {
  months: "flex flex-col",
  caption_label: "text-sm font-medium text-paper",
  nav_button: "h-7 w-7 bg-transparent p-0 text-gold hover:text-gold-light border border-gold/30 inline-flex items-center justify-center",
  head_cell: "text-ink/60 w-9 font-normal text-[0.72rem] uppercase",
  cell: "relative p-0 text-center text-sm",
  day: "h-9 w-9 p-0 font-normal text-paper hover:bg-gold/20 rounded-none inline-flex items-center justify-center transition-colors",
  day_selected: "!bg-gold !text-navy-900 hover:!bg-gold-light",
  day_today: "border border-gold/50 text-gold-light",
  day_outside: "text-ink/20",
  day_disabled: "!text-ink/20 line-through cursor-not-allowed hover:!bg-transparent",
};

export const DateTimePicker = ({ value, onChange, id }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");

  const commit = (d, t) => {
    if (d && t) {
      const [h, m] = t.split(":").map(Number);
      const slot = SLOTS.find((s) => s.value === t);
      onChange(`${format(d, "EEE, d MMM yyyy")} · ${slot ? slot.label : `${h}:${m}`}`);
    } else {
      onChange("");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          data-testid="enquiry-input-datetime"
          className="field-input flex items-center justify-between text-left"
        >
          <span className={value ? "text-paper" : "text-ink/30"}>
            {value || "Select a date & time"}
          </span>
          <CalendarIcon size={15} className="ml-2 shrink-0 text-gold" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        data-testid="datetime-popover"
        className="w-auto border border-gold/30 bg-navy-800 p-0 text-paper shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]"
      >
        <div className="flex flex-col sm:flex-row">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              setDate(d);
              commit(d, time);
            }}
            disabled={(d) => d < today || d.getDay() === 0}
            classNames={calendarClassNames}
            className="p-3"
          />
          <div className="border-t border-gold/15 p-3 sm:border-l sm:border-t-0" data-testid="datetime-slots">
            <p className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
              <Clock size={12} className="text-gold" /> Time
            </p>
            <div className="grid max-h-[240px] grid-cols-2 gap-1.5 overflow-y-auto pr-1 sm:w-40">
              {SLOTS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  disabled={!date}
                  data-testid={`time-slot-${s.value}`}
                  onClick={() => {
                    setTime(s.value);
                    commit(date, s.value);
                  }}
                  className={`border px-2 py-1.5 font-mono text-[11px] tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
                    time === s.value
                      ? "border-gold bg-gold text-navy-900"
                      : "border-white/10 text-ink hover:border-gold/50 hover:text-gold-light"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-gold/15 px-3 py-2.5">
          <button
            type="button"
            data-testid="datetime-clear"
            onClick={() => {
              setDate(undefined);
              setTime("");
              onChange("");
            }}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60 hover:text-gold-light"
          >
            Clear
          </button>
          <button
            type="button"
            data-testid="datetime-done"
            onClick={() => setOpen(false)}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-gold-light"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
